import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

const VALID_CONSENT_TYPES = [
  'terms_of_service',
  'data_processing',
  'ai_processing',
  'email_notifications',
  'analytics',
  'third_party_sharing',
] as const;

type ConsentType = typeof VALID_CONSENT_TYPES[number];

/**
 * GDPR Art. 7 - Conditions for Consent
 * GET: Returns current consent status for the user
 * POST: Grants or revokes a specific consent
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  const supabaseAdmin = getSupabaseAdminClient();

  if (req.method === 'GET') {
    try {
      // Get latest consent status for each type
      const { data: consents, error } = await supabaseAdmin
        .from('gdpr_consent_log')
        .select('consent_type, granted, granted_at, revoked_at, consent_text_version, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Build current consent map (latest entry per type wins)
      const consentMap: Record<string, {
        granted: boolean;
        granted_at: string | null;
        revoked_at: string | null;
        version: string;
        last_updated: string;
      }> = {};

      for (const consent of consents || []) {
        if (!consentMap[consent.consent_type]) {
          consentMap[consent.consent_type] = {
            granted: consent.granted,
            granted_at: consent.granted_at,
            revoked_at: consent.revoked_at,
            version: consent.consent_text_version,
            last_updated: consent.created_at,
          };
        }
      }

      // Fill in missing consent types as not granted
      for (const type of VALID_CONSENT_TYPES) {
        if (!consentMap[type]) {
          consentMap[type] = {
            granted: false,
            granted_at: null,
            revoked_at: null,
            version: '1.0',
            last_updated: '',
          };
        }
      }

      return res.status(200).json({
        user_id: user.id,
        consents: consentMap,
        available_types: VALID_CONSENT_TYPES,
      });
    } catch (error: any) {
      console.error('[GDPR Consent GET] Error:', error);
      return res.status(500).json({ error: 'Error al obtener consentimientos.' });
    }
  }

  if (req.method === 'POST') {
    try {
      const { consent_type, granted, version } = req.body || {};

      // Validate consent_type
      if (!consent_type || !VALID_CONSENT_TYPES.includes(consent_type)) {
        return res.status(400).json({
          error: `Tipo de consentimiento inválido. Tipos válidos: ${VALID_CONSENT_TYPES.join(', ')}`,
        });
      }

      if (typeof granted !== 'boolean') {
        return res.status(400).json({
          error: 'El campo "granted" debe ser true o false.',
        });
      }

      // Extract IP and User-Agent for audit
      const ipAddress = (req.headers['x-forwarded-for'] as string)?.split(',')[0]?.trim()
        || req.socket?.remoteAddress
        || null;
      const userAgent = req.headers['user-agent'] || null;

      const now = new Date().toISOString();

      const { error } = await supabaseAdmin.from('gdpr_consent_log').insert({
        user_id: user.id,
        consent_type,
        granted,
        ip_address: ipAddress,
        user_agent: userAgent,
        consent_text_version: version || '1.0',
        granted_at: granted ? now : null,
        revoked_at: !granted ? now : null,
      });

      if (error) throw error;

      // Log to audit trail
      await supabaseAdmin.from('auditoria_financiera').insert({
        usuario_id: user.id,
        usuario_nombre: user.email,
        usuario_rol: user.primaryRole,
        accion: granted ? 'consent_granted' : 'consent_revoked',
        entidad_tipo: 'gdpr_consent',
        entidad_id: user.id,
        descripcion: `Consentimiento "${consent_type}" ${granted ? 'otorgado' : 'revocado'} (v${version || '1.0'})`,
        datos_nuevos: { consent_type, granted, version: version || '1.0' },
      });

      return res.status(200).json({
        success: true,
        consent_type,
        granted,
        timestamp: now,
      });
    } catch (error: any) {
      console.error('[GDPR Consent POST] Error:', error);
      return res.status(500).json({ error: 'Error al registrar consentimiento.' });
    }
  }

  return res.status(405).json({ error: 'Method not allowed' });
}

export default withAuth(handler);
